import { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FileDropzone,
  FileInput,
  Header,
  Icon,
  Link,
  ProgressBar,
  SpaceBetween,
  Spinner,
  Table,
} from '@amzn/awsui-components-core';
import { generatePath } from 'react-router-dom';
import { AttachmentsGuidelineLink } from './AttachmentsGuidelineLink';
import {
  AttachmentRow,
  UploadingFile,
  useAttachmentsTabViewModel,
} from './AttachmentsTabViewModel';
import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import { AttachmentSnapshot } from '../../services/AttachmentManagementService';
import RouterPaths from '../../common/RouterPaths';

function buildDownloadPath(contentId: string, attachmentId: string): string {
  return generatePath(RouterPaths.GET_ATTACHMENTS, { contentId, attachmentId });
}

function tryDecodeFilename(filename: string): string | undefined {
  try {
    return decodeURIComponent(filename);
  } catch {
    return undefined;
  }
}

function snapshotFilename(snapshot: AttachmentSnapshot): string | undefined {
  if (snapshot.kind === 'scanning' || snapshot.kind === 'complete') {
    const raw = snapshot.attachment.filename;
    return tryDecodeFilename(raw) ?? raw;
  }
  return undefined;
}

function renderNameCell(row: AttachmentRow, contentId: string) {
  const filename = snapshotFilename(row.snapshot);
  if (filename === undefined) return <Spinner />;
  if (row.snapshot.kind === 'complete' && row.snapshot.attachment.scanResult === 'OK') {
    return (
      <Link href={buildDownloadPath(contentId, row.attachmentId)} external target="_blank">
        {filename}
      </Link>
    );
  }
  return <Box>{filename}</Box>;
}

function renderStatusCell(row: AttachmentRow) {
  const { snapshot } = row;
  if (snapshot.kind === 'unknown' || snapshot.kind === 'scanning') {
    return (
      <Box>
        <Spinner /> Scanning
      </Box>
    );
  }
  if (snapshot.kind === 'failed') {
    return (
      <Box color="text-status-error">
        <Icon name="status-negative" variant="error" /> Error
      </Box>
    );
  }
  if (snapshot.attachment.scanResult === 'INFECTED') {
    return (
      <Box color="text-status-warning">
        <Icon name="status-warning" variant="warning" /> Infected
      </Box>
    );
  }
  return (
    <Box color="text-status-success">
      <Icon name="status-positive" variant="success" /> OK
    </Box>
  );
}

interface UploadIndicatorRowProps {
  readonly entry: UploadingFile;
  readonly onClear: (id: string) => void;
}

function UploadIndicatorRow({ entry, onClear }: UploadIndicatorRowProps) {
  if (entry.status === 'loading') {
    return (
      <ProgressBar value={entry.progressPercent} label={entry.filename} status="in-progress" />
    );
  }
  return (
    <ProgressBar
      value={entry.progressPercent}
      label={entry.filename}
      status="error"
      resultText="Upload failed"
      resultButtonText="Clear"
      onResultButtonClick={() => onClear(entry.id)}
    />
  );
}

export const AttachmentsTabView = memo(function AttachmentsTabView() {
  const vm = useAttachmentsTabViewModel();

  if (!vm) return null;

  const handleFilesPicked = (files: readonly File[]) => {
    for (const file of files) {
      void vm.addAttachment(file);
    }
  };

  return (
    <>
      <SpaceBetween size="m">
        <Container
          header={
            <Header
              variant="h3"
              description="Add supplemental information such as diagram source files (.pptx), checklists (.xlsx), Word files (.docx), PDFs, or images. Do not attach scripts and code samples; use the Code tab instead."
              actions={<AttachmentsGuidelineLink />}
              counter={vm.rows.length > 0 ? `(${vm.rows.length})` : undefined}
            >
              Attachments
            </Header>
          }
        >
          <SpaceBetween size="m">
            {vm.rows.length > 0 && (
              <Table<AttachmentRow>
                columnDefinitions={[
                  { id: 'name', header: 'Name', cell: (row) => renderNameCell(row, vm.contentId) },
                  {
                    id: 'status',
                    header: 'Status',
                    width: 140,
                    minWidth: 140,
                    cell: renderStatusCell,
                  },
                  {
                    id: 'actions',
                    header: '',
                    width: 56,
                    minWidth: 56,
                    cell: (row) => (
                      <Button
                        variant="icon"
                        iconName="remove"
                        ariaLabel="Delete attachment"
                        onClick={() => vm.requestDelete(row.attachmentId)}
                      />
                    ),
                  },
                ]}
                items={vm.rows as AttachmentRow[]}
                trackBy="attachmentId"
                variant="embedded"
              />
            )}
            {vm.uploadingFiles.map((entry) => (
              <UploadIndicatorRow key={entry.id} entry={entry} onClear={vm.clearUploadEntry} />
            ))}
            <FileDropzone onChange={({ detail }) => handleFilesPicked(detail.value)}>
              <SpaceBetween size="s" alignItems="center">
                <Box color="inherit">Drop files here or select from below</Box>
                <FileInput
                  multiple
                  value={[]}
                  accept={vm.allowedFileExtensions.join(',')}
                  onChange={({ detail }) => handleFilesPicked(detail.value)}
                >
                  Choose files
                </FileInput>
                {vm.validationErrorMessage && (
                  <Alert
                    type="error"
                    dismissible
                    onDismiss={vm.clearValidationError}
                    statusIconAriaLabel="Error"
                  >
                    {vm.validationErrorMessage}
                  </Alert>
                )}
              </SpaceBetween>
            </FileDropzone>
          </SpaceBetween>
        </Container>
        <Alert type="warning">
          {
            'Do not attach scripts, templates, compiled code, or code projects. Use the Code tab to add those in a code repository.'
          }
        </Alert>
      </SpaceBetween>
      {vm.deleteConfirmation && (
        <ConfirmationModalView
          header="Delete attachment"
          body={
            <>
              Are you sure you want to delete{' '}
              {vm.deleteConfirmation.filename
                ? `"${vm.deleteConfirmation.filename}"`
                : 'this attachment'}
              ?
            </>
          }
          confirmLabel="Delete"
          isConfirming={false}
          onDismiss={vm.cancelDelete}
          onConfirm={vm.confirmDelete}
        />
      )}
    </>
  );
});
